import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Briefcase, Eye, ArrowRight, Warning, Trophy, Lightbulb,
  EnvelopeSimple, Sparkle, TrendUp, TrendDown, ChartLine
} from '@phosphor-icons/react';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString()}`;

const ReadinessBar = ({ score }) => {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 75 ? '#2D6A4F' : pct >= 50 ? '#C5A059' : '#C53030';
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

const DeltaPill = ({ delta }) => {
  if (delta === null || delta === undefined) {
    return <span className="text-xs text-[#A0AABF]">—</span>;
  }
  if (delta === 0) {
    return <span className="text-xs text-[#A0AABF]">flat</span>;
  }
  const positive = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${
        positive ? 'text-[#2D6A4F]' : 'text-[#C53030]'
      }`}
    >
      {positive ? <TrendUp size={12} /> : <TrendDown size={12} />}
      {positive ? '+' : ''}{fmtMoney(delta)}
    </span>
  );
};

const AdvisorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestSending, setDigestSending] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/advisor/dashboard');
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load advisor dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await api.get('/advisor/digest/preview');
      setDigestPreview(res.data);
      toast.success('Digest preview ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
  };

  const handleSend = async () => {
    setDigestSending(true);
    try {
      const res = await api.post('/advisor/digest/send');
      if (res.data.success) {
        toast.success('Weekly digest sent');
      } else {
        toast.info(res.data.message || 'Email service not configured — preview ready instead');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setDigestSending(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-[#4A5568]">Loading advisor dashboard...</div>;
  }

  const households = data?.households || [];
  const totals = {
    households: households.length,
    milestones: households.reduce((s, h) => s + (h.recent_milestones?.length || 0), 0),
    opportunities: households.reduce((s, h) => s + (h.opportunities?.length || 0), 0),
    needs_attention: households.filter(h => h.needs_attention).length,
  };

  return (
    <div className="space-y-8 fade-in">
      {/* Banner */}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Advisor Dashboard:</strong> Read-only multi-client overview. Households remain in control — owners may revoke advisor access at any time from the Household page. Informational only — not personalized investment advice.
          </p>
        </div>
      </div>

      {/* Header + Digest Actions */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Advisor Dashboard
          </h1>
          <p className="text-base text-[#4A5568]">
            {totals.households === 0
              ? 'No advised households yet — request an invite from a client to begin.'
              : `${totals.households} advised household${totals.households !== 1 ? 's' : ''} · last 7-day window`}
          </p>
        </div>
        {totals.households > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handlePreview}
              data-testid="advisor-digest-preview-button"
              className="border-[#0A1128] text-[#0A1128] w-full sm:w-auto"
            >
              <Eye size={18} className="mr-2" />
              Preview Digest
            </Button>
            <Button
              onClick={handleSend}
              disabled={digestSending}
              data-testid="advisor-digest-send-button"
              className="bg-[#0A1128] hover:bg-[#152145] w-full sm:w-auto"
            >
              <EnvelopeSimple size={18} className="mr-2" />
              {digestSending ? 'Sending...' : 'Send Weekly Digest'}
            </Button>
          </div>
        )}
      </div>

      {/* Stats Strip */}
      {totals.households > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0A1128] text-white rounded-md p-5" data-testid="advisor-stat-households">
            <div className="flex items-center gap-2 text-[#C5A059] text-xs uppercase tracking-[0.1em] font-semibold mb-2">
              <Briefcase size={16} weight="duotone" />
              Households
            </div>
            <p className="text-3xl font-semibold ibm-plex">{totals.households}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card" data-testid="advisor-stat-milestones">
            <div className="flex items-center gap-2 text-[#C5A059] text-xs uppercase tracking-[0.1em] font-semibold mb-2">
              <Trophy size={16} weight="duotone" />
              Milestones (7d)
            </div>
            <p className="text-3xl font-semibold text-[#0A1128] ibm-plex">{totals.milestones}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card" data-testid="advisor-stat-opportunities">
            <div className="flex items-center gap-2 text-[#C5A059] text-xs uppercase tracking-[0.1em] font-semibold mb-2">
              <Lightbulb size={16} weight="duotone" />
              Opportunities
            </div>
            <p className="text-3xl font-semibold text-[#0A1128] ibm-plex">{totals.opportunities}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card" data-testid="advisor-stat-attention">
            <div className="flex items-center gap-2 text-[#C53030] text-xs uppercase tracking-[0.1em] font-semibold mb-2">
              <Warning size={16} weight="duotone" />
              Need Attention
            </div>
            <p className="text-3xl font-semibold text-[#C53030] ibm-plex">{totals.needs_attention}</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {totals.households === 0 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-12 text-center" data-testid="advisor-empty-state">
          <Briefcase size={48} weight="duotone" className="text-[#C5A059] mx-auto mb-4" />
          <h3 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-2">No advised households yet</h3>
          <p className="text-[#4A5568] max-w-md mx-auto">
            Ask a household owner to invite you with the <strong>Read-Only Advisor</strong> role from their Household page. You'll automatically appear here once the invitation is accepted.
          </p>
        </div>
      )}

      {/* Household Cards */}
      <div className="space-y-4">
        {households.map((h) => (
          <div
            key={h.household_id}
            className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card"
            data-testid={`advisor-household-card-${h.household_id}`}
          >
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#0A1128] text-white flex items-center justify-center cabinet-grotesk text-lg font-medium">
                  {(h.household_name || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">
                    {h.household_name}
                  </h3>
                  <p className="text-sm text-[#4A5568]">
                    {h.primary_user?.full_name} {h.primary_user?.email && <span className="text-[#A0AABF]">· {h.primary_user.email}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {h.needs_attention && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#C53030]/10 text-[#C53030] text-xs font-semibold uppercase tracking-[0.08em] rounded">
                    <Warning size={14} weight="duotone" />
                    Needs Attention
                  </span>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate(`/advisor/household/${h.household_id}`)}
                  data-testid={`advisor-open-household-${h.household_id}`}
                  className="border-[#0A1128] text-[#0A1128]"
                >
                  Open <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Net Worth</p>
                <p className="text-xl font-semibold text-[#0A1128] ibm-plex">{fmtMoney(h.combined_net_worth)}</p>
                <DeltaPill delta={h.net_worth_delta} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Wealth Health Score™</p>
                <p className="text-xl font-semibold text-[#0A1128] ibm-plex">{h.wealth_health_score}<span className="text-sm text-[#A0AABF]">/100</span></p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Retirement Readiness</p>
                <p className="text-xl font-semibold text-[#0A1128] ibm-plex mb-1">{h.retirement_readiness}%</p>
                <ReadinessBar score={h.retirement_readiness} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Recent Milestones</p>
                <p className="text-xl font-semibold text-[#C5A059] ibm-plex">{h.recent_milestone_count}</p>
                <p className="text-xs text-[#A0AABF]">last 7 days</p>
              </div>
            </div>

            {h.opportunities && h.opportunities.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-2 flex items-center gap-1.5">
                  <Lightbulb size={14} weight="duotone" className="text-[#C5A059]" />
                  Planning Opportunities ({h.opportunities.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {h.opportunities.slice(0, 3).map((o, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2.5 py-1 bg-[#C5A059]/15 text-[#0A1128] text-xs font-medium rounded"
                    >
                      {o.headline}
                    </span>
                  ))}
                  {h.opportunities.length > 3 && (
                    <span className="text-xs text-[#4A5568] py-1">+{h.opportunities.length - 3} more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Digest Preview Modal-lite */}
      {digestPreview && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6" data-testid="advisor-digest-preview-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">Weekly Digest Preview</h2>
              <p className="text-sm text-[#4A5568] mt-1">
                {new Date(digestPreview.week_start).toLocaleDateString()} – {new Date(digestPreview.week_end).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={() => setDigestPreview(null)}
              data-testid="advisor-digest-close-preview"
              className="text-[#4A5568] hover:text-[#0A1128] text-sm"
            >
              Close
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-[#4A5568] uppercase">Households</p>
              <p className="text-lg font-semibold ibm-plex">{digestPreview.totals.household_count}</p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-[#4A5568] uppercase">Milestones</p>
              <p className="text-lg font-semibold ibm-plex">{digestPreview.totals.milestones}</p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-[#4A5568] uppercase">Opportunities</p>
              <p className="text-lg font-semibold ibm-plex">{digestPreview.totals.opportunities}</p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-[#4A5568] uppercase">Attention</p>
              <p className="text-lg font-semibold ibm-plex text-[#C53030]">{digestPreview.totals.needs_attention}</p>
            </div>
          </div>
          <p className="text-xs text-[#4A5568]">
            Click "Send Weekly Digest" to deliver this summary to <strong>{digestPreview.advisor.email}</strong>. If Resend is not configured, only the preview is available.
          </p>
        </div>
      )}

      <p className="text-xs text-[#4A5568] leading-relaxed">
        <strong>Informational Use Only:</strong> The advisor dashboard surfaces shared household data and general planning patterns. It is not personalized investment, tax, or legal advice. Advisors must validate insights against each household's full circumstances and applicable professional standards.
      </p>
    </div>
  );
};

export default AdvisorDashboard;
